import { format } from 'date-fns';
import { api, getApiError } from './api';
import { mapAttendanceRecord, mapMonthlySummary } from './mappers';
import { geolocationService } from './geolocation';
import type { AttendanceRecord, MonthlySummary } from '../types';

type PaginatedResponse = {
  data: Record<string, unknown>[];
};

export const attendanceService = {
  async getTodayStatus(): Promise<AttendanceRecord | null> {
    const { data } = await api.get<{ data: Record<string, unknown> | null }>('/me/attendance/today');
    if (!data.data) return null;
    return mapAttendanceRecord(data.data);
  },

  async checkIn(photoDataUrl?: string): Promise<AttendanceRecord> {
    const location = await geolocationService.getCurrentPosition();
    const payload: Record<string, unknown> = {
      latitude: location.latitude,
      longitude: location.longitude,
      accuracy: location.accuracy,
      device: navigator.userAgent.substring(0, 200),
    };
    if (photoDataUrl) {
      payload.photo = photoDataUrl;
    }
    try {
      const { data } = await api.post<{ data: Record<string, unknown> }>('/me/attendance/check-in', payload);
      return mapAttendanceRecord(data.data);
    } catch (err) {
      throw new Error(getApiError(err));
    }
  },

  async checkOut(photoDataUrl?: string): Promise<AttendanceRecord> {
    const location = await geolocationService.getCurrentPosition();
    const payload: Record<string, unknown> = {
      latitude: location.latitude,
      longitude: location.longitude,
      accuracy: location.accuracy,
      device: navigator.userAgent.substring(0, 200),
    };
    if (photoDataUrl) {
      payload.photo = photoDataUrl;
    }
    try {
      const { data } = await api.post<{ data: Record<string, unknown> }>('/me/attendance/check-out', payload);
      return mapAttendanceRecord(data.data);
    } catch (err) {
      throw new Error(getApiError(err));
    }
  },

  async getAttendanceHistory(startDate?: Date, endDate?: Date): Promise<AttendanceRecord[]> {
    const params: Record<string, string> = { per_page: '100' };
    if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
    if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');
    const { data } = await api.get<{ data: PaginatedResponse }>('/me/attendance/history', { params });
    const items = data.data?.data ?? [];
    return items.map(mapAttendanceRecord);
  },

  async getMonthlySummary(date: Date = new Date()): Promise<MonthlySummary> {
    const { data } = await api.get<{ data: Record<string, unknown> }>('/me/attendance/summary', {
      params: { month: date.getMonth() + 1, year: date.getFullYear() },
    });
    return mapMonthlySummary(data.data);
  },
};

